import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../common/Card';
import Button from '../common/Button';
import api from '../../services/api';
import { formatDate, formatCurrency } from '../../utils/formatters';
import '../../styles/admin.css';

const BusinessDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadBusiness();
  }, [id]);

  const loadBusiness = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/admin/businesses/${id}`);
      setBusiness(response.data);
    } catch (error) {
      console.error('Failed to load business:', error);
      setError('Failed to load business details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading">Loading business details...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!business) return <div className="not-found">Business not found</div>;

  return (
    <div className="business-details">
      <div className="details-header">
        <Button variant="secondary" onClick={() => navigate('/admin/businesses')}>
          ← Back to Businesses
        </Button>
        <h1>{business.business_name}</h1>
      </div>

      <div className="details-grid">
        <Card title="Business Information">
          <div className="info-row">
            <label>Email:</label>
            <span>{business.business_email}</span>
          </div>
          <div className="info-row">
            <label>Phone:</label>
            <span>{business.business_phone || '-'}</span>
          </div>
          <div className="info-row">
            <label>Address:</label>
            <span>{business.business_address || '-'}</span>
          </div>
          <div className="info-row">
            <label>Tax Number:</label>
            <span>{business.tax_number || '-'}</span>
          </div>
          <div className="info-row">
            <label>Created:</label>
            <span>{formatDate(business.created_at)}</span>
          </div>
        </Card>

        <Card title="Subscription Details">
          <div className="info-row">
            <label>Plan:</label>
            <span>{business.plan_name || 'No Plan'}</span>
          </div>
          <div className="info-row">
            <label>Status:</label>
            <span className={`status-badge ${business.subscription_status}`}>
              {business.subscription_status}
            </span>
          </div>
          <div className="info-row">
            <label>Trial Ends:</label>
            <span>{business.trial_ends_at ? formatDate(business.trial_ends_at) : 'N/A'}</span>
          </div>
        </Card>

        <Card title="Statistics">
          <div className="info-row">
            <label>Total Users:</label>
            <span>{business.user_count || 0}</span>
          </div>
          <div className="info-row">
            <label>Total Products:</label>
            <span>{business.product_count || 0}</span>
          </div>
          <div className="info-row">
            <label>Total Customers:</label>
            <span>{business.customer_count || 0}</span>
          </div>
          <div className="info-row">
            <label>Total Sales:</label>
            <span>{formatCurrency(business.total_sales || 0)}</span>
          </div>
        </Card>

        {business.recent_users && business.recent_users.length > 0 && (
          <Card title="Recent Users">
            <table className="recent-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {business.recent_users.map(user => (
                  <tr key={user.id}>
                    <td>{user.first_name} {user.last_name}</td>
                    <td>{user.username}</td>
                    <td>{user.email}</td>
                    <td>
                      <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </div>
  );
};

export default BusinessDetails;